import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'styled-components';
import { AuthProvider } from './contexts/AuthContext';
import { GlobalStyle } from './styles/GlobalStyle';
import { theme } from './styles/theme';

// Layout
import MainLayout from './layouts/MainLayout';

// Auth Pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Unauthorized from './pages/Unauthorized';

// Content Pages
import HomePage from './pages/Home';
import CoursesPage from './pages/Courses';
import CourseDetailPage from './pages/Courses/CourseDetail';
import LessonPage from './pages/Courses/Lesson';
import CommunityPage from './pages/Community';
import PostDetail from './pages/Community/PostDetail';
import ProfilePage from './pages/Profile/ProfileView';
import ProfileEditPage from './pages/Profile/ProfileEdit';

// Admin Pages
import AdminDashboard from './pages/Admin';

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute';
import { withRoleAccess } from './components/auth/withRoleAccess';
import { ROLES } from './hooks/useAuthorization';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protect admin routes
const ProtectedAdminCourses = withRoleAccess([ROLES.ADMIN, ROLES.APP_EDITOR])(CoursesPage);
const ProtectedAdminCourseEdit = withRoleAccess([ROLES.ADMIN, ROLES.APP_EDITOR])(CourseDetailPage);
const ProtectedAdminLessonEdit = withRoleAccess([ROLES.ADMIN, ROLES.APP_EDITOR])(LessonPage);

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <AuthProvider>
          <Router>
            <Routes>
              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              
              {/* Main Layout Routes */}
              <Route element={<MainLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/community" element={<CommunityPage />} />
                <Route path="/community/posts/:postId" element={<PostDetail />} />
                
                {/* Protected Routes */}
                <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
                  {/* Public Course Routes */}
                  <Route path="/courses" element={<CoursesPage />} />
                  <Route path="/courses/:courseId" element={<CourseDetailPage />} />
                  <Route path="/courses/:courseId/lessons/:lessonId" element={<LessonPage />} />
                  
                  {/* Course Management Routes */}
                  <Route path="/admin/courses" element={<ProtectedAdminCourses />} />
                  <Route path="/admin/courses/new" element={<ProtectedAdminCourseEdit />} />
                  <Route path="/admin/courses/:courseId/edit" element={<ProtectedAdminCourseEdit />} />
                  <Route path="/admin/courses/:courseId/lessons/new" element={<ProtectedAdminLessonEdit />} />
                  <Route path="/admin/courses/:courseId/lessons/:lessonId/edit" element={<ProtectedAdminLessonEdit />} />
                  
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/profile/edit" element={<ProfileEditPage />} />
                </Route>
              </Route>
              
              {/* Admin Routes */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requireEditor>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              
              {/* Fallback Route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
